//REACT AND REACT BASED LIBRARIES
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSWRMutation from "swr/mutation";
//COMPONENTS
import Table from "@components/Table";
import { Box, Container } from "@mui/material";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import BackgroundImage from "@components/BackgroundImageCst";
import CustomRowAction from "@components/Table/components/CustomRowAction";
//CUSTOM HOOKS AND UTILS
import { ServicesURL } from "@utils/utilsApi";
import { MethodType } from "src/services/type";
import { PagesID } from "@utils/utilsConfigurations";
import { fetcher } from "src/services/config";
import { ActionsType, ColumnsType } from "src/interfaces/Common";
import { useFetchTable } from "@hooks/useFetchDataTable";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import ButtonBackPage from "@components/ButtonBackPage";
import { useFilters } from "@context/filters";
import { useAuth } from "@context/auth";

const TaggingSW: React.FC = () => {
  const { t } = useTranslation(["tagging-page"]);
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["sw-tagging"]}`;

  const navigate = useNavigate();

  const [idTaggingDeleted, setIdTaggingDeleted] = useState<string>();
  const [openModalDelete, setOpenModalDelete] = useState(false);

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  let newAssociatedServices = {};
  if (filters[filterId]?.associatedServices?.length > 0) {
    newAssociatedServices = {
      associatedServices: filters[filterId].associatedServices
        ?.map((type: any) => {
          return (type?.value).toString().toUpperCase();
        })
        .join(","),
    };
  }

  const { columns, dataTable, actionsTopToolbar, actionForRow, mutate } =
    useFetchTable(
      `${ServicesURL.getVersioningTagging}`,
      PagesID["sw-tagging"],
      filters[filterId]
        ? { ...filters[filterId], ...newAssociatedServices }
        : { pageIndex: 0, pageSize: 20 },
    );

  const { dataTable: dataVersioning } = useFetchTable(
    `${ServicesURL.getVersioning}`,
    PagesID["sw-micro-services"],
  );

  const configurationColumnOptions: ColumnsType[] | undefined =
    React.useMemo(() => {
      if (dataVersioning && dataVersioning?.response) {
        let allServices = dataVersioning?.response.map((response: any) => {
          return { label: response.name, value: response.versioningId };
        });
        allServices = allServices.sort((a: any, b: any) => {
          if (a.label.toLowerCase() < b.label.toLowerCase()) return -1;
          if (a.label.toLowerCase() > b.label.toLowerCase()) return 1;
          return 0;
        });
        return columns?.map((column: any) => {
          switch (column.accessorKey) {
            case "associatedServices": {
              if (allServices.length > 0) {
                return {
                  ...column,
                  options: allServices,
                };
              } else {
                return {
                  ...column,
                  hasFilter: false,
                };
              }
            }
            default:
              return column;
          }
        });
      } else {
        return columns;
      }
    }, [columns, dataTable]);

  const { trigger: triggerDelete } = useSWRMutation(
    `${ServicesURL.deleteVersioningTagging}`,
    (url: string) => {
      return fetcher<any>(`${url}`, MethodType.DELETE, {
        tagId: idTaggingDeleted,
      });
    },
  );

  const handleDelete = React.useCallback(async () => {
    if (idTaggingDeleted) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerDelete();
        const newData = [
          ...dataTable.response.filter(
            (el: any) => el.tagId !== idTaggingDeleted,
          ),
        ];
        mutate && mutate({ ...dataTable, ...{ output: newData } });
        setOpenModalDelete(!openModalDelete);
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setIdTaggingDeleted(undefined);
        setOpenToast(undefined);
        console.error(error);
      }
    }
  }, [dataTable, idTaggingDeleted, mutate, triggerDelete]);

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "edit": {
          return {
            ...action,
            callBack: (row: any) => {
              navigate(`edit/${row.tagId}`);
            },
          };
        }
        case "link": {
          return {
            ...action,
            callBack: (row: any) => {
              navigate(`${row.tagId}/services`);
            },
          };
        }
        case "delete": {
          return {
            ...action,
            callBack: (currentRow: any) => {
              setIdTaggingDeleted(currentRow?.tagId);
              setOpenModalDelete(true);
            },
          };
        }
        default:
          return action;
      }
    });
  }, [actionForRow]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-certificates.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${t("hero-title")}`}
          description={t("hero-description")}
        />
        <ButtonBackPage />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          <Table
            id={PagesID["sw-tagging"]}
            data={dataTable?.response || []}
            columns={configurationColumnOptions || []}
            configurationTopToolbar={actionsTopToolbar}
            renderRowActions={customRenderRowActions}
            totalPages={dataTable?.metadata?.total_elements}
            modalTitle={t("hero-title")?.replace("-", "") || ""}
          />
        </Box>
      </Container>

      <ModalConfirmsCst
        open={!!openModalDelete}
        title={t("delete-item", { ns: "modal" })}
        description={t("confirm-delete", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenModalDelete(!openModalDelete);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => handleDelete()}
        status={openToast}
      />
      <ModalConfirmsCst
        open={!!openToast}
        title={t("hero-title-add")}
        description={""}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default TaggingSW;
